import React from 'react';
import DynamicTable from '@atlaskit/dynamic-table';
import { useQuery } from '@tanstack/react-query';
import { EventDTO, SloEventsDTO } from '@atlassiansox/phobos-client-ts';
import EmptyState from '@atlaskit/empty-state';
import css from '@emotion/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner';

import { DateDisplayFormats } from '@/constants';
import { KeyValue, BreachCount, DetailButtonDialog, LinkWithRef } from '@/components/primitive';
import { fields } from '@/components/primitive/editable-report/constants';
import { apiFetch } from '@/services';
import { formatDateForDisplay } from '@/utils';
import { querySpec } from '@/services/api-service/api-queries';
import { useFetchErrorController } from '@/controllers';
import { FixCapabilitySLOEvents } from '@/api';
import {
  eventTableStyles,
  flex,
  flexDirectionColumn,
  colors,
  spaceGapCozy,
  flexCenter,
  spaceGapCompact,
} from '@/styles';

import EventAnnotations from '../event-annotations';

// Atlaskit InlineDialog sets 448px as max-width, here we want to always extend the width
// to that also use a large size(width) modal if any slo event has annotation
const breachDialogStyle = css`
  & > div {
    min-width: 448px;
    max-width: 640px;
  }
`;

export const pastBreachingList = css`
  padding-left: 0;
  li {
    list-style: none;
    ${flex}
    ${flexDirectionColumn}
    ${spaceGapCozy}
    thead {
      border-bottom: none !important;
      background: ${token('color.background.disabled', colors.N100)};
    }
  }
`;

const BreachingSLOs: React.FC<{ title?: string; breaches?: number; capabilityId: string }> = ({
  title = 'Breaching SLOs',
  breaches,
  capabilityId,
}) => {
  const { onError } = useFetchErrorController();

  const { queryKey: eventsKey, path: eventsPath } = querySpec('capability.{id}.events.summary', {
    id: capabilityId,
  });
  const { isInitialLoading: isLoadingEvents, data: eventsData } = useQuery(
    eventsKey,
    () => apiFetch(eventsPath),
    { onError }
  );

  //  Filter for breaches that are open
  const filterEvents = (s: SloEventsDTO): SloEventsDTO => ({
    slo: s.slo,
    events: s.events!.filter((e: EventDTO) => e.type === 'BREACH' && !e.end),
  });

  const sloEventsData =
    !isLoadingEvents && eventsData
      ? FixCapabilitySLOEvents(
          eventsData!.map(filterEvents).filter((s: SloEventsDTO) => s.events!.length > 0)
        )
      : [];

  if (isLoadingEvents) {
    return (
      <span css={[flexCenter, spaceGapCompact]}>
        <Spinner size="small" />
        <KeyValue title={title}>
          <BreachCount count={breaches!} />
        </KeyValue>
      </span>
    );
  }

  return (
    <DetailButtonDialog
      css={breachDialogStyle}
      testId="open-slo-breaches"
      title={
        <KeyValue title={title}>
          <BreachCount count={breaches} />
        </KeyValue>
      }
    >
      {sloEventsData?.length > 0 ? (
        <ul css={pastBreachingList}>
          {sloEventsData.map((sloEvents: SloEventsDTO) => {
            // Display annotation field regardless of whether there is an
            // annotation or not. If there isn't one, it will show an add button
            const sloBreachFields = fields.breaches;

            return (
              <li key={sloEvents.slo!.publicId}>
                <LinkWithRef href={`/slo/${sloEvents.slo!.publicId}`}>
                  <h5>{sloEvents.slo!.name}</h5>
                </LinkWithRef>
                <span css={eventTableStyles}>
                  <DynamicTable
                    head={{ cells: sloBreachFields }}
                    rows={sloEvents.events!.map(event => ({
                      cells: sloBreachFields.map(({ key }) => {
                        if (key === 'start' || key === 'end') {
                          const date = event[key];
                          return {
                            key: date?.getTime()?.toString(),
                            content: date ? (
                              formatDateForDisplay(date, DateDisplayFormats.TIME_WITH_NUMERIC_DATE)
                            ) : (
                              <strong>Ongoing</strong>
                            ),
                          };
                        }
                        if (key === 'annotation') {
                          return {
                            key,
                            content: <EventAnnotations event={event} />,
                          };
                        }
                        return {};
                      }),
                    }))}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          header="No breach events"
          description={<>This capability has no open or recent breach events.</>}
          imageUrl="/assets/generic-empty-state.svg"
        />
      )}
    </DetailButtonDialog>
  );
};

export default BreachingSLOs;
